import React from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"

interface CsvDialogProps {
  open: boolean
  onClose: () => void
  title: string
  csvContent: string
  downloadFilename: string
  titleExtra?: React.ReactNode
}

const parseCsvToRows = (csv: string): string[][] => {
  const lines = csv.trim().split("\n")
  return lines.map((line) => line.split(",").map((cell) => cell.trim()))
}

export const CsvDialog = ({
  open,
  onClose,
  title,
  csvContent,
  downloadFilename,
  titleExtra
}: CsvDialogProps) => {
  const handleDownload = () => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", downloadFilename)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderContent = () => {
    const rows = parseCsvToRows(csvContent)
    if (rows.length === 0) return <Typography variant="body2">No data</Typography>
    const [headerRow, ...dataRows] = rows
    return (
      <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
        <TableHead>
          <TableRow>
            {headerRow.map((cell, i) => (
              <TableCell key={i} sx={{ fontWeight: 600, borderWidth: "2px", whiteSpace: "nowrap" }}>{cell}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {dataRows.map((row, i) => (
            <TableRow key={i}>
              {row.map((cell, j) => (
                <TableCell key={j} sx={{ borderWidth: "2px" }}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: "8px"
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "21px" }}>
          {title}
        </Typography>
        {titleExtra}
      </DialogTitle>
      <DialogContent sx={{ pb: 1, overflowX: "auto" }}>{renderContent()}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
        <Button onClick={handleDownload} variant="contained" > Download CSV </Button>
        <Button onClick={onClose} variant="outlined" color="secondary" >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
